import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "field" }
const _hoisted_2 = {
  key: 0,
  class: "block p-error text-sm"
}

import { reactive, withDefaults, onUpdated, ref, onMounted, nextTick } from "vue";
import Dropdown, { DropdownChangeEvent } from "primevue/dropdown";
import { ICustomer } from "@/clinic/typings";
import { ValidateError } from "async-validator";
import * as _ from "lodash";
import { set } from "@vueuse/core";
import {
  PLACEHOLDER_SELECT,
  CUSTOMER_ID_NAME_NAME_KANA_PHONE_PLACEHOLDER_SELECT,
} from "@/clinic/typings/constants/clinic";

type ICustomerFieldExisted = {
  id: number | null;
  customers: ICustomer[];
  errorFields: Record<string, ValidateError[]>;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'existed',
  props: {
    id: { default: null },
    customers: null,
    errorFields: null
  },
  emits: ["change"],
  setup(__props: any, { emit: emits }) {

const props = __props as { id: number | null, customers: ICustomer[], errorFields: Record<string, ValidateError[]> };





const form = reactive<{ id: number | null }>({
  id: props?.id,
});

const dropdownRef = ref<any>();
const currentFilterValue = ref("");
const isComposing = ref(false);
const enterPressedDuringComposition = ref(false);
const isJapaneseInput = ref(false);

const onChanging = ({ value: newCustomerId }: DropdownChangeEvent) => {
  emits("change", newCustomerId);
};

const handleEnterKeyPress = () => {
  const filterInput = dropdownRef.value?.$el?.querySelector(".p-dropdown-filter") as HTMLInputElement;
  const searchValue = (filterInput?.value || currentFilterValue.value).toLowerCase().trim();

  if (!searchValue) return;

  const filtered = props.customers.filter(customer => {
    const searchFields = [
      customer.code?.toLowerCase(),
      customer.name?.toLowerCase(),
      customer.name_kana?.toLowerCase(),
      customer.phone,
    ].filter(Boolean);

    return searchFields.some(field => field?.includes(searchValue));
  });

  if (filtered.length > 0) {
    form.id = filtered[0].id;
    emits("change", filtered[0].id);
    dropdownRef.value?.hide();
  }
};

// 日本語入力後のエンターキー処理（最初の候補を自動選択）
const handleJapaneseEnterKeyPress = () => {
  const filterInput = dropdownRef.value?.$el?.querySelector(".p-dropdown-filter") as HTMLInputElement;
  const searchValue = (filterInput?.value || currentFilterValue.value).toLowerCase().trim();

  if (!searchValue) return;

  const filtered = props.customers.filter(customer => {
    const searchFields = [
      customer.code?.toLowerCase(),
      customer.name?.toLowerCase(),
      customer.name_kana?.toLowerCase(),
      customer.phone,
    ].filter(Boolean);

    return searchFields.some(field => field?.includes(searchValue));
  });

  if (filtered.length > 0) {
    // 日本語入力時も最初の候補を自動選択してDropdownを閉じる
    // これにより安定した動作を実現
    form.id = filtered[0].id;
    emits("change", filtered[0].id);

    // 少し遅延させてからDropdownを閉じる（表示の安定性向上）
    setTimeout(() => {
      dropdownRef.value?.hide();
    }, 10);
  }
};

const loadId = () => {
  if (props.id !== form.id) {
    set(form, "id", props.id);
  }
};

onUpdated(() => {
  loadId();
});

const setupFilterInputListener = () => {
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const element = node as Element;
          const panel = element.classList?.contains("p-dropdown-panel")
            ? element
            : element.querySelector?.(".p-dropdown-panel");

          if (panel) {
            const filterInput = panel.querySelector("input.p-dropdown-filter") as HTMLInputElement;
            if (filterInput && !filterInput.hasAttribute("data-enter-listener")) {
              // IME（日本語入力）の開始を検知
              filterInput.addEventListener("compositionstart", () => {
                isComposing.value = true;
              });

              // IME（日本語入力）の終了を検知
              filterInput.addEventListener("compositionend", (e: CompositionEvent) => {
                isComposing.value = false;

                // 日本語入力が行われたことを記録
                if (e.data) {
                  isJapaneseInput.value = true;
                }

                // エンターキーで変換確定された場合は自動的に検索実行
                if (enterPressedDuringComposition.value && e.data) {
                  setTimeout(() => {
                    handleJapaneseEnterKeyPress();
                  }, 10);
                }

                // フラグをリセット
                enterPressedDuringComposition.value = false;
              });

              // エンターキーの処理
              filterInput.addEventListener("keydown", (e: KeyboardEvent) => {
                if (e.key === "Enter" || e.keyCode === 13) {
                  // IME変換中の場合は変換確定フラグを立てる
                  if (isComposing.value) {
                    enterPressedDuringComposition.value = true;
                    return;
                  }
                  // IME変換中でない場合は検索実行
                  e.preventDefault();
                  e.stopPropagation();
                  handleEnterKeyPress();
                }
              });
              filterInput.setAttribute("data-enter-listener", "true");
              observer.disconnect();
              return;
            }
          }
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), 5000);
};

const onFilterChange = (event: { value: string }) => {
  currentFilterValue.value = event.value || "";
  // フィルタ値が変更されたら日本語入力フラグをリセット
  isJapaneseInput.value = false;
  nextTick(() => setupFilterInputListener());
};

onMounted(() => {
  nextTick(() => setupFilterInputListener());
});

return (_ctx: any,_cache: any) => {
  const _component_InputText = _resolveComponent("InputText")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_InputText, {
      value: form.id,
      class: "hidden",
      name: "customer_id"
    }, null, 8, ["value"]),
    _createVNode(_unref(Dropdown), {
      ref_key: "dropdownRef",
      ref: dropdownRef,
      filter: "",
      filterFields: ['code', 'name', 'phone', 'name_kana'],
      modelValue: form.id,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((form.id) = $event)),
      options: props.customers,
      optionValue: "id",
      optionLabel: "name",
      class: "mr-2 max-w-6rem font-normal min-w-250",
      inputProps: { class: 'font-light text-black20 text-sm' },
      placeholder: _unref(PLACEHOLDER_SELECT),
      filterPlaceholder: _unref(CUSTOMER_ID_NAME_NAME_KANA_PHONE_PLACEHOLDER_SELECT),
      onChange: onChanging,
      onFilter: onFilterChange
    }, {
      option: _withCtx(({ option }) => [
        _createTextVNode(_toDisplayString(option.code) + " " + _toDisplayString(option.name), 1)
      ]),
      _: 1
    }, 8, ["modelValue", "options", "placeholder", "filterPlaceholder"]),
    (__props.errorFields?.customerId)
      ? (_openBlock(), _createElementBlock("small", _hoisted_2, _toDisplayString(_.head(__props.errorFields.customerId)?.message), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})